import React from "react";
import {
  Box,
  Button,
  Chip,
  Container,
  FormControl,
  FormControlLabel,
  Checkbox,
  Grid,
  InputAdornment,
  InputLabel,
  MenuItem,
  Pagination,
  Paper,
  Select,
  TextField,
  Typography,
  CircularProgress,
  Alert,
} from "@mui/material";
import {
  Add,
  ArrowBack,
  Clear,
  ContentCopy,
  Search,
  UploadFile,
} from "@mui/icons-material";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import PageTitle from "../common/PageTitle";
import {
  useBookmarkableHubState,
  type BookmarkableHubStateOptions,
} from "../../hooks/useBookmarkableHubState";
import { EntityStatus } from "../../types";
import { PM_DASHBOARD_PATH } from "../../utils/pmHubRoutes";
import {
  type HubPageSize,
  hubPageSizeToParam,
  hubShowsPagination,
  hubTotalPages,
} from "../../utils/hubPagination";

const PAGE_SIZE = 9;
const DEFAULT_STATUS_OPTIONS = Object.values(EntityStatus);

export interface EntityHubLayoutProps {
  hubTitle: string;
  hubDescription: string;
  addPath?: string;
  addLabel: string;
  onAdd?: () => void;
  onBulkUpload?: () => void;
  bulkUploadLabel?: string;
  headerActions?: React.ReactNode;
  loading?: boolean;
  error?: string | null;
  itemCount: number;
  children: React.ReactNode;
  bookmarkOptions?: BookmarkableHubStateOptions;
  filterSidebarExtra?: React.ReactNode;
  hubState: ReturnType<typeof useBookmarkableHubState>;
  statusOptions?: string[];
  /** When set with pageSizeOptions, enables per-page selector and pagination sizing */
  pageSize?: HubPageSize;
  onPageSizeChange?: (size: HubPageSize) => void;
  pageSizeOptions?: HubPageSize[];
  backPath?: string;
  backLabel?: string;
}

export { PAGE_SIZE };
export type { HubPageSize };

const EntityHubLayout: React.FC<EntityHubLayoutProps> = ({
  hubTitle,
  hubDescription,
  addPath,
  addLabel,
  onAdd,
  onBulkUpload,
  bulkUploadLabel = "Bulk upload",
  headerActions,
  loading,
  error,
  itemCount,
  children,
  filterSidebarExtra,
  hubState,
  statusOptions = DEFAULT_STATUS_OPTIONS,
  pageSize: pageSizeProp,
  onPageSizeChange,
  pageSizeOptions,
  backPath = PM_DASHBOARD_PATH,
  backLabel = "Back to Product Manager Dashboard",
}) => {
  const navigate = useNavigate();
  const {
    q,
    setQ,
    statuses,
    toggleStatus,
    page,
    setPage,
    clearAll,
    searchString,
    version,
    setVersion,
    approval,
    setApproval,
  } = hubState;

  const effectivePageSize = pageSizeProp ?? PAGE_SIZE;
  const totalPages = hubTotalPages(itemCount, effectivePageSize);
  const showPagination = hubShowsPagination(itemCount, effectivePageSize);
  const showPageSizeControl =
    Boolean(pageSizeOptions?.length && onPageSizeChange);

  const handleAdd = () => {
    if (onAdd) {
      onAdd();
      return;
    }
    if (!addPath) return;
    navigate({
      pathname: addPath,
      search: searchString || undefined,
    });
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
    } catch {
      /* ignore */
    }
  };

  const hasFilters =
    q.trim() ||
    statuses.length > 0 ||
    version.trim() ||
    approval !== null;

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "background.default" }}>
      <PageTitle pageTitle={hubTitle} pageDescription={hubDescription} />

      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Button
          component={RouterLink}
          to={backPath}
          startIcon={<ArrowBack />}
          sx={{ mb: 2, textTransform: "none" }}
        >
          {backLabel}
        </Button>

        <Grid container spacing={3}>
          <Grid item xs={12} md={3}>
            <Paper
              elevation={0}
              sx={{
                p: 2,
                borderRadius: 2,
                border: "1px solid",
                borderColor: "grey.200",
                position: { md: "sticky" },
                top: 100,
              }}
            >
              <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                Search & filters
              </Typography>

              <TextField
                fullWidth
                size="small"
                placeholder="Search..."
                value={q}
                onChange={(e) => setQ(e.target.value)}
                sx={{ mb: 2 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />

              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                Status
              </Typography>
              {statusOptions.map((status) => (
                <FormControlLabel
                  key={status}
                  control={
                    <Checkbox
                      size="small"
                      checked={statuses.includes(status)}
                      onChange={() => toggleStatus(status)}
                    />
                  }
                  label={<Typography variant="body2">{status}</Typography>}
                />
              ))}

              {filterSidebarExtra}

              {hasFilters && (
                <Button
                  fullWidth
                  size="small"
                  startIcon={<Clear />}
                  onClick={clearAll}
                  sx={{ mt: 2, textTransform: "none" }}
                >
                  Clear all
                </Button>
              )}

              {statuses.map((s) => (
                <Chip
                  key={s}
                  label={s}
                  size="small"
                  onDelete={() => toggleStatus(s)}
                  sx={{ mr: 0.5, mt: 1 }}
                />
              ))}

              {version.trim() && (
                <Chip
                  label={`Version: ${version}`}
                  size="small"
                  onDelete={() => setVersion("")}
                  sx={{ mr: 0.5, mt: 1 }}
                />
              )}

              {approval === true && (
                <Chip
                  label="Approval needed"
                  size="small"
                  onDelete={() => setApproval(null)}
                  sx={{ mr: 0.5, mt: 1 }}
                />
              )}

              {approval === false && (
                <Chip
                  label="No approval needed"
                  size="small"
                  onDelete={() => setApproval(null)}
                  sx={{ mr: 0.5, mt: 1 }}
                />
              )}
            </Paper>
          </Grid>

          <Grid item xs={12} md={9}>
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: 2,
                alignItems: "center",
                justifyContent: "space-between",
                mb: 3,
              }}
            >
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, alignItems: "center" }}>
                <Typography variant="body2" color="text.secondary">
                  {loading
                    ? "Loading..."
                    : `${itemCount} result${itemCount === 1 ? "" : "s"}`}
                  {!loading &&
                    effectivePageSize !== "all" &&
                    showPagination &&
                    ` · page ${Math.min(page, totalPages)} of ${totalPages}`}
                  {!loading && effectivePageSize === "all" && itemCount > 0 &&
                    " · showing all"}
                </Typography>
                {showPageSizeControl && (
                  <FormControl size="small" sx={{ minWidth: 110 }}>
                    <InputLabel id="hub-page-size-label">Per page</InputLabel>
                    <Select
                      labelId="hub-page-size-label"
                      label="Per page"
                      value={hubPageSizeToParam(effectivePageSize)}
                      onChange={(e) => {
                        const raw = e.target.value;
                        onPageSizeChange?.(
                          raw === "all" ? "all" : parseInt(raw, 10),
                        );
                      }}
                    >
                      {pageSizeOptions!.map((opt) => (
                        <MenuItem
                          key={hubPageSizeToParam(opt)}
                          value={hubPageSizeToParam(opt)}
                        >
                          {opt === "all" ? "All" : opt}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
              </Box>

              <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                <Button
                  size="small"
                  startIcon={<ContentCopy />}
                  onClick={handleCopyLink}
                  sx={{ textTransform: "none" }}
                >
                  Copy link
                </Button>
                {onBulkUpload && (
                  <Button
                    variant="outlined"
                    startIcon={<UploadFile />}
                    onClick={onBulkUpload}
                    sx={{ textTransform: "none" }}
                  >
                    {bulkUploadLabel}
                  </Button>
                )}
                {headerActions}
                <Button
                  variant="contained"
                  startIcon={<Add />}
                  onClick={handleAdd}
                  sx={{ textTransform: "none" }}
                >
                  {addLabel}
                </Button>
              </Box>
            </Box>

            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            {loading ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
                <CircularProgress />
              </Box>
            ) : (
              <>
                {children}
                {showPagination && (
                  <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
                    <Pagination
                      count={totalPages}
                      page={Math.min(page, totalPages)}
                      onChange={(_, p) => setPage(p)}
                      color="primary"
                    />
                  </Box>
                )}
              </>
            )}
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default EntityHubLayout;
